/**
 * EV Plug contact form (canvas "Contact op desktop"). Markup: snippets/m_contact-form.liquid.
 *
 * <m-contact-form> is Shopify's native contact form with topic chips, the order number field (only
 * for Bestelling or Retour), the company name field (only for Zakelijk), the "Over:" page chip,
 * validation and a success state. Texts come from
 * data attributes (evplug.contact_form.*).
 *
 * Sending: this script only checks the fields; the post itself stays Shopify's own. With spam
 * protection on, Shopify's hCaptcha script (bound to every contact form) adds its token and posts the
 * form; the page comes back with ?contact_posted=true and the success state, and the window reopens
 * itself (a session flag holding only the form id). No fetch: hCaptcha posts with the native
 * form.submit(), so a script-side post would miss the token or post twice. The submit check runs in
 * the capture phase, before hCaptcha's own listener, so an invalid or repeated submit never reaches it.
 *
 * <m-contact-dialog> (snippets/m_contact-dialog.liquid) wraps the form in a native <dialog>. On a
 * computer (hover + fine pointer) it catches clicks on every WhatsApp link of the theme and opens
 * the window instead; phones and tablets keep WhatsApp. Links inside app blocks (the kentekencheck
 * app carries its own WhatsApp link) or inside [data-contact-keep-whatsapp] are left alone. On a
 * page with the inline form (Klantenservice) the click scrolls to that form instead.
 * On a computer the same links also change their text: the phrase in data-label-whatsapp ("Stuur ons
 * een WhatsApp") becomes data-label-message ("Stuur ons een bericht"), also in rich text and product
 * descriptions and in content added later. Only that exact phrase changes; .ev-on-touch text is skipped.
 */
(() => {
  const COMPUTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const KEEP_WHATSAPP = [
    '.shopify-app-block',
    '[data-evf2]',
    '[data-evplug-vehicle-finder]',
    '[data-evplug-compatibility-root]',
    '.evplug-results',
    '[data-contact-keep-whatsapp]'
  ].join(',');
  const RETURN_KEY = 'evplug-contact-return';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Pages whose URL can carry a token or personal data: their URL is never sent along.
  const PRIVATE_PATH = /\/(account|orders|checkouts|challenge|apps)\//i;
  // Re-enable the button if the post never leaves (e.g. the visitor closed the hCaptcha puzzle).
  const UNLOCK_AFTER = 15000;

  /**
   * Whether a link points to WhatsApp (wa.me, api.whatsapp.com, web.whatsapp.com, …).
   * @param {HTMLAnchorElement} link - The link.
   * @returns {boolean}
   */
  function isWhatsAppLink(link) {
    try {
      const { hostname } = new URL(link.href, window.location.href);
      return hostname === 'wa.me' || hostname === 'whatsapp.com' || hostname.endsWith('.whatsapp.com');
    } catch (error) {
      return false;
    }
  }

  /**
   * Escapes text for use inside a regular expression.
   * @param {string} text - Plain text.
   * @returns {string}
   */
  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Gives `text` the same first-letter case as `model` ("Stuur…" or "stuur…").
   * @param {string} model - The text being replaced.
   * @param {string} text - The replacement.
   * @returns {string}
   */
  function matchCase(model, text) {
    const first = model.charAt(0);
    const upper = first !== first.toLowerCase();
    return (upper ? text.charAt(0).toUpperCase() : text.charAt(0).toLowerCase()) + text.slice(1);
  }

  /**
   * The current page for the "Over:" chip: origin and path only (no query string or hash), and
   * nothing at all on account, order, checkout, spam-check or app pages.
   * @returns {string}
   */
  function safePageUrl() {
    const { origin, pathname } = window.location;
    if (PRIVATE_PATH.test(`${pathname}/`)) return '';
    return `${origin}${pathname}`.slice(0, 300);
  }

  /** Session flag: which form was just sent (only its id), so the page can show it again on return. */
  const returnFlag = {
    set(id) {
      try {
        window.sessionStorage.setItem(RETURN_KEY, id);
      } catch (error) {
        // Private mode: the message still goes out, only the window won't reopen on return.
      }
    },
    take(id) {
      try {
        if (window.sessionStorage.getItem(RETURN_KEY) !== id) return false;
        window.sessionStorage.removeItem(RETURN_KEY);
        return true;
      } catch (error) {
        return false;
      }
    }
  };

  /**
   * Stops a submit before it reaches Shopify's listeners (hCaptcha and the captcha bootstrap).
   * @param {SubmitEvent} event - Submit event.
   */
  function stopSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  if (!customElements.get('m-contact-form')) {
    customElements.define('m-contact-form', class extends HTMLElement {
      connectedCallback() {
        if (this.form) return;
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.form.noValidate = true;
        this.panel = this.querySelector('[data-cf-panel]');
        this.success = this.querySelector('[data-cf-success]');
        this.alert = this.querySelector('[data-cf-alert]');
        this.submitButton = this.querySelector('[data-cf-submit]');
        this.openButton = this.querySelector('[data-cf-open]');
        this.orderField = this.querySelector('[data-cf-order]');
        this.companyField = this.querySelector('[data-cf-company]');
        this.context = this.querySelector('[data-cf-context]');
        this.message = this.querySelector('[data-cf-field="message"]');
        this.trap = this.querySelector('[data-cf-hp]');
        this.submitLabel = this.submitButton ? this.submitButton.textContent : '';
        this.defaultPlaceholder = this.message ? this.message.placeholder : '';

        // Capture phase on the wrapper: runs before any listener on the form itself.
        this.addEventListener('submit', this.onSubmit.bind(this), true);
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-topic]')) this.onTopic();
        });
        this.addEventListener('input', (event) => {
          if (this.tried && event.target.matches('[data-cf-field]')) this.validate();
        });
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-cf-context-remove]')) this.removeContext();
          if (event.target.closest('[data-cf-open]')) this.reveal({ scroll: false });
        });
        // Back to the page from the browser cache: the button works again.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) this.unlock();
        });

        // Klantenservice on phones: the "Mail" button above the questions opens this form.
        if (this.hasAttribute('data-cf-inline')) {
          document.addEventListener('click', (event) => {
            const link = event.target.closest(`a[data-cf-show="${this.id}"]`);
            if (!link) return;
            event.preventDefault();
            this.reveal();
          });
        }

        // Product pages: keep the SKU in the "Over:" chip in step with the chosen variant.
        if (this.querySelector('[data-cf-sku-input]')) {
          document.addEventListener('on:variant:change', (event) => {
            const variant = event.detail && event.detail.variant;
            const sku = variant && variant.sku ? String(variant.sku).slice(0, 60) : '';
            this.querySelector('[data-cf-sku-input]').value = sku;
            const skuText = this.querySelector('[data-cf-sku]');
            if (skuText) skuText.textContent = sku;
            const skuWrap = this.querySelector('[data-cf-sku-wrap]');
            if (skuWrap) skuWrap.hidden = !sku;
          });
        }

        this.onTopic();
        // Shopify's captcha script refills the form after its spam check (on DOMContentLoaded).
        if (document.readyState !== 'complete') {
          document.addEventListener('DOMContentLoaded', () => this.onTopic(), { once: true });
        }

        // Shopify renders the sent state on every contact form after ?contact_posted=true.
        const sent = this.success && !this.success.hidden;
        if (sent) {
          this.classList.add('is-sent', 'is-open');
          if (this.panel) this.panel.hidden = true;
        }

        // Back from our own post (sent, refilled after the spam check, or with an error from Shopify).
        const params = new URLSearchParams(window.location.search);
        const refilled = params.get('form_type') === 'contact' && params.has('form_key');
        const failed = this.alert && !this.alert.hidden;
        this.returned = returnFlag.take(this.id) && (sent || refilled || failed);
        if (!this.returned) return;

        if (sent && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'evplug_contact_form_sent',
            contact_source: this.hasAttribute('data-cf-inline') ? 'page' : 'window'
          });
        }
        if (this.hasAttribute('data-cf-inline')) {
          this.classList.add('is-open');
          this.scrollIntoView({ block: 'start' });
        }
        // The redirect keeps the form's #id, and the browser's jump to it on load takes the focus
        // away again: focus the message (or the form) once the page has loaded.
        const refocus = () => {
          if (sent) {
            this.querySelector('[data-cf-success-title]').focus({ preventScroll: true });
          } else {
            this.focusFirst();
          }
        };
        refocus();
        if (document.readyState !== 'complete') {
          window.addEventListener('load', () => window.setTimeout(refocus), { once: true });
        }
      }

      /** The checked topic radio, if any. */
      get topic() {
        return this.querySelector('[data-topic]:checked');
      }

      /** Topic chips: the placeholder follows the topic; the order number only for order or return, the company name only for business. */
      onTopic() {
        const topic = this.topic;
        const id = topic ? topic.dataset.topic : '';
        if (this.message) this.message.placeholder = topic ? topic.dataset.placeholder : this.defaultPlaceholder;

        if (this.orderField) {
          const show = id === 'order' || id === 'return';
          this.orderField.hidden = !show;
          this.orderField.querySelector('input').disabled = !show;
        }

        if (this.companyField) {
          const show = id === 'business';
          this.companyField.hidden = !show;
          this.companyField.querySelector('input').disabled = !show;
        }
      }

      /** Removes the "Over:" chip: its page fields are no longer sent. */
      removeContext() {
        if (!this.context) return;
        this.context.hidden = true;
        this.context.querySelectorAll('[data-cf-context-input]').forEach((input) => {
          input.disabled = true;
        });
        this.message.focus();
      }

      /**
       * Opens a collapsed card (phones and tablets) and brings the form into view.
       * @param {object} [options] - { scroll: true } scrolls the form into view.
       */
      reveal({ scroll = true } = {}) {
        if (this.classList.contains('is-sent')) this.reset();
        this.classList.add('is-open');
        if (this.openButton) this.openButton.setAttribute('aria-expanded', 'true');
        if (scroll) {
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        }
        this.focusFirst();
      }

      /** Focuses the first field: the topic chips when none is chosen, otherwise the name. */
      focusFirst() {
        const target = this.topic
          ? this.querySelector('[data-cf-field="name"]')
          : this.querySelector('[data-topic]');
        if (target) target.focus({ preventScroll: true });
      }

      /** Back to an empty form after a sent message (the window can be opened again). */
      reset() {
        this.unlock();
        this.form.reset();
        this.tried = false;
        this.validate(false);
        this.success.hidden = true;
        this.querySelector('[data-cf-success-body]').hidden = true;
        this.panel.hidden = false;
        this.classList.remove('is-sent');
        if (this.context) {
          this.context.hidden = false;
          this.context.querySelectorAll('[data-cf-context-input]').forEach((input) => {
            input.disabled = false;
          });
        }
        this.onTopic();
      }

      /**
       * Checks name, email and message, and shows or clears the errors.
       * @param {boolean} [show=true] - Show the errors (false only clears them).
       * @returns {HTMLElement|null} The first invalid field, or null.
       */
      validate(show = true) {
        let first = null;
        this.querySelectorAll('[data-cf-field]').forEach((field) => {
          const value = field.value.trim();
          let invalid = !value;
          if (field.dataset.cfField === 'email') {
            const emailText = this.querySelector('[data-cf-email-error]');
            if (emailText) emailText.textContent = value ? this.dataset.errorEmailInvalid : this.dataset.errorEmail;
            invalid = invalid || !EMAIL_PATTERN.test(value);
          }
          invalid = show && invalid;
          if (invalid && !first) first = field;
          field.setAttribute('aria-invalid', String(invalid));
          const error = this.querySelector(`#${field.id}-error`);
          if (error) error.hidden = !invalid;
        });
        this.showAlert(first ? this.dataset.errorSummary : '');
        return first;
      }

      /**
       * Shows or hides the red message above the form.
       * @param {string} text - Message, or '' to hide it.
       */
      showAlert(text) {
        if (!this.alert) return;
        this.alert.querySelector('[data-cf-alert-text]').textContent = text;
        this.alert.hidden = !text;
      }

      /**
       * Checks the form before Shopify (and its hCaptcha) posts it. Exactly one post per click:
       * invalid, repeated or bot submits are stopped here and never reach Shopify's listeners.
       * @param {SubmitEvent} event - Submit event (capture phase).
       */
      onSubmit(event) {
        if (event.target !== this.form) return;
        if (this.busy) {
          stopSubmit(event);
          return;
        }

        this.tried = true;
        const invalid = this.validate();
        if (invalid) {
          stopSubmit(event);
          invalid.focus();
          return;
        }

        // Honeypot: only bots fill this hidden field. Show the sent state, send nothing.
        if (this.trap && this.trap.value) {
          stopSubmit(event);
          this.showSuccess();
          return;
        }

        // Only ever post to the shop's own /contact (the action Shopify renders).
        if (new URL(this.form.action, window.location.href).origin !== window.location.origin) {
          stopSubmit(event);
          this.showAlert(this.dataset.errorSend);
          return;
        }

        this.lock();
        returnFlag.set(this.id);

        // Shopify's captcha script holds the post back while hCaptcha isn't ready (or didn't load):
        // then nothing was sent, so the visitor can try again.
        window.setTimeout(() => {
          if (event.defaultPrevented && !this.form.hasAttribute('data-hcaptcha-bound')) {
            this.unlock();
            this.showAlert(this.dataset.errorSend);
            this.submitButton.focus();
          }
        });
      }

      /** While the message is on its way: button disabled, empty optional fields left out of the email. */
      lock() {
        this.busy = true;
        this.form.setAttribute('aria-busy', 'true');
        this.submitButton.disabled = true;
        this.submitButton.textContent = this.dataset.sending;
        this.emptied = [...this.form.querySelectorAll('input[name^="contact["], textarea[name^="contact["]')]
          .filter((field) => !field.disabled && field.type !== 'radio' && field.value === '');
        this.emptied.forEach((field) => {
          field.disabled = true;
        });
        window.clearTimeout(this.unlockTimer);
        this.unlockTimer = window.setTimeout(() => this.unlock(), UNLOCK_AFTER);
      }

      unlock() {
        window.clearTimeout(this.unlockTimer);
        if (!this.busy) return;
        this.busy = false;
        this.form.setAttribute('aria-busy', 'false');
        this.submitButton.disabled = false;
        this.submitButton.textContent = this.submitLabel;
        (this.emptied || []).forEach((field) => {
          field.disabled = false;
        });
        this.emptied = [];
        this.onTopic();
      }

      /** Sent state without a post (honeypot): looks the same as a real one. */
      showSuccess() {
        const name = this.querySelector('[data-cf-field="name"]').value.trim().split(/\s+/)[0] || '';
        const email = this.querySelector('[data-cf-field="email"]').value.trim();

        // Function replacers: "$&" and the like in a name or address stay literal text.
        this.querySelector('[data-cf-success-title]').textContent = this.dataset.sentTitle.replace('[name]', () => name);
        const body = this.querySelector('[data-cf-success-body]');
        body.textContent = this.dataset.sentBody.replace('[email]', () => email);
        body.hidden = false;

        this.form.reset();
        this.tried = false;
        this.panel.hidden = true;
        this.success.hidden = false;
        this.classList.add('is-sent');
        this.querySelector('[data-cf-success-title]').focus();
      }
    });
  }

  if (!customElements.get('m-contact-dialog')) {
    customElements.define('m-contact-dialog', class extends HTMLElement {
      connectedCallback() {
        if (this.dialog) return;
        this.dialog = this.querySelector('dialog');
        this.contactForm = this.querySelector('m-contact-form');
        if (!this.dialog || !this.contactForm) return;

        // Close with the X, "Sluiten" or a click on the backdrop (press and release outside the window).
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-cf-close]')) this.close();
        });
        this.dialog.addEventListener('pointerdown', (event) => {
          this.pressedBackdrop = event.target === this.dialog;
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog && this.pressedBackdrop) this.close();
        });
        this.dialog.addEventListener('close', () => {
          if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
          this.opener = null;
        });

        document.addEventListener('click', this.onLinkClick.bind(this), true);

        // Link texts: "Stuur ons een WhatsApp" reads "Stuur ons een bericht" on computers.
        const { labelWhatsapp, labelMessage } = this.dataset;
        if (labelWhatsapp && labelMessage) {
          this.labelPattern = new RegExp(escapeRegExp(labelWhatsapp), 'giu');
          this.labelMessage = labelMessage;
          const start = () => {
            if (this.labelObserver) return;
            this.relabel(document.body);
            this.labelObserver = new MutationObserver((records) => {
              records.forEach((record) => {
                record.addedNodes.forEach((node) => {
                  if (node.nodeType === Node.ELEMENT_NODE) this.relabel(node);
                });
              });
            });
            this.labelObserver.observe(document.body, { childList: true, subtree: true });
          };
          if (COMPUTER.matches) {
            start();
          } else {
            COMPUTER.addEventListener('change', () => { if (COMPUTER.matches) start(); });
          }
        }

        // Back from a post made in this window: show it again (sent, refilled or with Shopify's error).
        if (this.contactForm.returned) this.open(null, { keepSent: true });
      }

      /**
       * Swaps the WhatsApp phrase for the message phrase in the WhatsApp links inside `root` (or
       * `root` itself), for the links this window catches.
       * @param {Element} root - Element to search.
       */
      relabel(root) {
        const links = root.matches('a[href]') ? [root] : root.querySelectorAll('a[href]');
        links.forEach((link) => {
          if (!isWhatsAppLink(link) || link.closest(KEEP_WHATSAPP)) return;
          const walker = document.createTreeWalker(link, NodeFilter.SHOW_TEXT);
          for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            if (node.parentElement && node.parentElement.closest('.ev-on-touch')) continue;
            const text = node.nodeValue;
            const swapped = text.replace(this.labelPattern, (match) => matchCase(match, this.labelMessage));
            if (swapped !== text) node.nodeValue = swapped;
          }
        });
      }

      /**
       * Catches clicks on theme WhatsApp links on computers.
       * @param {MouseEvent} event - Click event (capture phase).
       */
      onLinkClick(event) {
        if (!COMPUTER.matches) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest && event.target.closest('a[href]');
        if (!link || !isWhatsAppLink(link) || link.closest(KEEP_WHATSAPP)) return;

        event.preventDefault();

        // Klantenservice: the form is on the page already.
        const inline = document.querySelector('m-contact-form[data-cf-inline]');
        if (inline && inline.getClientRects().length > 0) {
          inline.reveal();
          return;
        }

        this.open(link);
      }

      /**
       * Opens the window.
       * @param {Element|null} opener - Element to focus again on close.
       * @param {object} [options] - { keepSent: true } keeps the sent state.
       */
      open(opener, { keepSent = false } = {}) {
        if (this.dialog.open) return;
        const form = this.contactForm;
        if (form.classList.contains('is-sent') && !keepSent) form.reset();

        const url = form.querySelector('[data-cf-url]');
        if (url) {
          url.value = safePageUrl();
          url.disabled = !url.value || (form.context && form.context.hidden);
        }

        this.opener = opener;
        this.dialog.showModal();
        if (form.classList.contains('is-sent')) {
          form.querySelector('[data-cf-success-title]').focus();
        } else {
          form.focusFirst();
        }
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }
    });
  }
})();
